import { DateTime } from 'luxon';

import { getSortedTiers } from 'app/(trade)/queries/perpsNewUserTradeIncentive.utils';
import { centsToCompactMoney } from 'utils/rewardsMoney';

import type {
    FeeReductionOffer,
    FeeShareOffer,
    FeeShareReward,
    PerpsFeeShareReferral,
    PerpsProgramSummary,
    PerpsReferrerProgram,
    PerpsVolumeTieredReferral,
    ProductSummary,
    ReferralStatus,
    ReferralsSummaryResponse,
    RewardDetails,
    SentReferral,
    UnacknowledgedReferralsTray,
} from './referralsSummary';

// Compact + "?" treatment for the navbar/sign-up gift pill under variable
// rewards (e.g. "$2k?") — the referee doesn't know the actual amount until claim.
export const formatVariableRewardsMaxReward = (cents: number): string => `${centsToCompactMoney(cents)}?`;

const UNDER_THRESHOLD_STATUSES: ReadonlySet<ReferralStatus> = new Set([
    'account_created',
    'account_verified',
    'has_deposited',
    'trades_placed',
]);

export const getWasReferredButUnderThreshold = (summary: ReferralsSummaryResponse | undefined): boolean => {
    const status = summary?.referee?.referee?.status;
    return !!status && UNDER_THRESHOLD_STATUSES.has(status);
};

export const getWasReferredUnderVariableRewards = (summary: ReferralsSummaryResponse | undefined): boolean =>
    summary?.referee?.reward_type === 'variable';

/**
 * The caller-as-referee's own offer: the per-referral snapshot when the backend sends it,
 * else the program's current offer — the same approximation mobile's BFF ships (stale for
 * grandfathered/bespoke-code referees until the snapshot lands server-side).
 */
export const getRefereeOfferRewardDetails = (
    summary: ReferralsSummaryResponse | undefined,
): RewardDetails | undefined =>
    summary?.referee?.referee?.reward_details ?? summary?.referrer?.predictions?.current_offer?.referee.reward_details;

export const getRefereeFlatRewardCents = (summary: ReferralsSummaryResponse | undefined): number =>
    getRefereeOfferRewardDetails(summary)?.flat_reward?.payout_amount_cents ?? 0;

export const getRefereeMaxRewardCents = (summary: ReferralsSummaryResponse | undefined): number =>
    getRefereeOfferRewardDetails(summary)?.variable_reward?.max_reward_cents ?? 0;

export const getRefereeThresholdCents = (summary: ReferralsSummaryResponse | undefined): number =>
    summary?.referee?.referee?.threshold_cents ?? 0;

export type ReferrerEligibility = 'eligible' | 'below_minimum' | 'cannot_refer';

export const getReferrerEligibility = (program: ProductSummary | undefined): ReferrerEligibility => {
    if (program?.eligible) return 'eligible';
    // BE populates eligibility_progress only while under the referrer trade threshold
    if (program?.eligibility_progress) return 'below_minimum';
    return 'cannot_refer';
};

export const isOverLifetimeCap = (program: ProductSummary | undefined): boolean => {
    if (!program) return false;
    const { amount_cents, number_of_referrals } = program.lifetime_cap;
    if (amount_cents !== null && program.total_earned_cents !== null && program.total_earned_cents >= amount_cents) {
        return true;
    }
    return number_of_referrals !== null && (program.total_credited_referrals ?? 0) >= number_of_referrals;
};

// Server-gated to variable rewards; re-claiming a claimed reward 422s.
export const isSentReferralClaimable = (referral: SentReferral): boolean => referral.claim_available;

// Keeps reading `status`: callers only reach this inside a `state === 'completed'` branch, so
// substituting `state` makes it a tautology and kills the below_minimum/cannot_refer arms;
// `!claim_available` collapses the same way, being true for every flat row. The terminal step's
// status is the real replacement — deferred for lack of coverage, not for lack of a BE field.
// variable rewards track the claim on variable_claim; flat rewards only have referee.status
export const hasReferrerClaimedReward = (referral: SentReferral): boolean =>
    referral.referrer.variable_claim?.has_claimed || referral.referee.status === 'reward_claimed';

export const getWasReferred = (summary: ReferralsSummaryResponse | undefined): boolean => summary?.referee != null;

// Server-gated: narrower than an issued `status`, since it also requires variable rewards and an
// unblocked referrer.
export const getRefereeHasClaimableReward = (summary: ReferralsSummaryResponse | undefined): boolean =>
    summary?.referee?.referee?.claim_available ?? false;

export const hasClaimableReferralRewards = (summary: ReferralsSummaryResponse | undefined): boolean =>
    getRefereeHasClaimableReward(summary) ||
    (summary?.referrer?.predictions?.referrals.some(isSentReferralClaimable) ?? false);

export const getFlatReferrerRewardCents = (program: ProductSummary | undefined): number | null =>
    program?.current_offer?.referrer.reward_details.flat_reward?.payout_amount_cents ?? null;

export const getVariableMaxRewardCents = (program: ProductSummary | undefined): number | null =>
    program?.current_offer?.referrer.reward_details.variable_reward?.max_reward_cents ?? null;

/**
 * Tier-progress numbers for a referral's ladder: granted/total counts ("Tier X of N" copy) and
 * the 1-based position of the in-progress tier in the threshold-sorted ladder ("Tier {n} offer
 * expires …" copy). `inProgressPosition` is keyed off `in_progress_tier_key` — not derived as
 * `granted + 1` — so it's null whenever no tier is in progress or the key doesn't resolve, even
 * if ungranted tiers remain.
 */
export const getTierProgress = (
    referral: PerpsVolumeTieredReferral,
): { granted: number; total: number; inProgressPosition: number | null } => {
    const reward = referral.referrer.volume_tiered_reward;
    const inProgressIndex =
        reward.in_progress_tier_key === null
            ? -1
            : getSortedTiers(reward.tiers).findIndex((tier) => tier.key === reward.in_progress_tier_key);
    return {
        granted: reward.tiers.filter((tier) => tier.granted).length,
        total: reward.tiers.length,
        inProgressPosition: inProgressIndex === -1 ? null : inProgressIndex + 1,
    };
};

/**
 * Max cumulative reward per referral from the current offer — drives "Earn up to $X per referral".
 * Null when the perps block is absent (gated no-op backend) or the offer ladder is empty
 * (ineligible referrers get `reward_details: {}`).
 */
export const getMaxRewardPerReferral = (summary: ReferralsSummaryResponse): number | null =>
    summary.referrer?.perps?.current_offer?.referrer.reward_details.volume_tiered_offer?.max_cumulative_reward_cents ??
    null;

export const isPerpsFeeShare = (summary: ReferralsSummaryResponse | undefined): boolean =>
    summary?.referrer?.perps?.current_offer?.reward_type === 'fee_share';

export const getPerpsFeeShareOffer = (summary: ReferralsSummaryResponse | undefined): FeeShareOffer | null =>
    summary?.referrer?.perps?.current_offer?.referrer.reward_details.fee_share_offer ?? null;

export const getPerpsReferrerFlatRewardCents = (summary: ReferralsSummaryResponse | undefined): number | null =>
    summary?.referrer?.perps?.current_offer?.referrer.reward_details.flat_offer?.reward_amount_cents ?? null;

export const getPerpsFeeReductionOffer = (summary: ReferralsSummaryResponse | undefined): FeeReductionOffer | null =>
    summary?.referrer?.perps?.current_offer?.referee.reward_details.fee_reduction_offer ?? null;

export const getPerpsWeeklyEarnedCents = (perps: PerpsReferrerProgram | undefined): number | null =>
    perps?.reward_details?.fee_share?.weekly_earned_cents ?? null;

/** The referrer's entry for one program id, or null when the response doesn't carry it. */
export const getPerpsProgramSummary = (
    perps: PerpsReferrerProgram | undefined,
    programId: string | null,
): PerpsProgramSummary | null => {
    if (!programId) return null;
    return perps?.programs?.find((program) => program.program_id === programId) ?? null;
};

/** The program entry backing the live fee-share offer, discriminated the same way as the render branch so it can't disagree with which program is "current." */
export const getPerpsFeeShareProgramSummary = (perps: PerpsReferrerProgram | undefined): PerpsProgramSummary | null =>
    perps?.programs?.find((program) => program.current_offer?.reward_type === 'fee_share') ?? null;

/** Every field is assigned unconditionally — a program that omits one must read as absent, never fall back to the merged value. */
export const applyPerpsProgramScope = (
    perps: PerpsReferrerProgram,
    program: PerpsProgramSummary,
): PerpsReferrerProgram => ({
    ...perps,
    total_earned_cents: program.total_earned_cents,
    total_credited_referrals: program.total_credited_referrals,
    pending: program.pending,
    pending_count: program.pending_count,
    completed_count: program.completed_count,
    expired_count: program.expired_count,
    referral_offer_available: program.referral_offer_available,
    current_offer: program.current_offer,
    referrals: program.referrals,
    reward_details: program.reward_details,
    // Referrer-level, not per-program — the backend omits it from ProgramSummary for the same
    // reason. Left unset here it would leak the live offer's progress into a scoped/ended view.
    eligibility_progress: undefined,
});

/** Narrow a program's rows to the volume-tiered variant (rows are program-homogeneous per summary). */
export const getPerpsVolumeTieredReferrals = (program: PerpsReferrerProgram | undefined): PerpsVolumeTieredReferral[] =>
    (program?.referrals ?? []).filter(
        (referral): referral is PerpsVolumeTieredReferral => referral.reward_type === 'volume_tiered',
    );

export const getPerpsFeeShareReferrals = (program: PerpsReferrerProgram | undefined): PerpsFeeShareReferral[] =>
    (program?.referrals ?? []).filter(
        (referral): referral is PerpsFeeShareReferral => referral.reward_type === 'fee_share',
    );

/** Pre-qualification rows still working through the funnel. */
export const isPerpsFeeShareReferralPending = (referral: PerpsFeeShareReferral): boolean =>
    referral.referee.state === 'pending';

/** Qualified rows with the share window active. */
export const isPerpsFeeShareReferralEarning = (referral: PerpsFeeShareReferral): boolean =>
    referral.referee.state === 'completed';

/** True once the reward reaches the per-referral cap. */
export const isPerpsFeeShareMaxEarned = (reward: FeeShareReward): boolean =>
    reward.max_reward_cents > 0 && reward.reward_earned_cents >= reward.max_reward_cents;

/** 0..1 fill of the reward-vs-cap bar; 0 when the cap is unknown. */
export const getPerpsFeeShareRewardFraction = (reward: FeeShareReward): number => {
    if (reward.max_reward_cents <= 0) return 0;
    return Math.min(Math.max(reward.reward_earned_cents / reward.max_reward_cents, 0), 1);
};

/**
 * The unacknowledged referral rewards tray, or null when absent at any nesting level.
 */
export const getUnacknowledgedReferralTray = (summary: ReferralsSummaryResponse): UnacknowledgedReferralsTray | null =>
    summary.referrer?.perps?.reward_details?.volume_tiered?.unacknowledged_rewards_tray ?? null;

/**
 * True when the referral's ladder is non-empty and every tier is granted — drives the
 * "All bonuses unlocked" copy.
 */
export const isReferralFullyGranted = (referral: PerpsVolumeTieredReferral): boolean => {
    const tiers = referral.referrer.volume_tiered_reward.tiers;
    return tiers.length > 0 && tiers.every((tier) => tier.granted);
};

/**
 * Check before getDaysUntilExpiry — it floors at 0, so a stale expiry would otherwise read as
 * "today". `referee.state` alone isn't enough: the backend labels a referral 'completed' once its
 * window closes and it earned at least one tier, even if the ladder never finished.
 */
export const isPerpsReferralExpired = (referral: PerpsVolumeTieredReferral, now: DateTime = DateTime.now()): boolean =>
    referral.referee.state === 'expired' || DateTime.fromISO(referral.expiration_ts) < now;

export type PerpsTierSegment = {
    key: string;
    granted: boolean;
    /** 0..1 fill of this segment: 1 when granted, partial within the in-progress tier, else 0. */
    fillFraction: number;
};

/**
 * Segment model for a referral's mini tier-progress bar, threshold-ascending. Each segment fills
 * proportionally to the volume traded between the previous tier's threshold and its own — so every
 * crossed segment shows full, not just the in-progress one (the bar tracks the friend's volume, not
 * the grant-driven `in_progress_tier_key`, which stalls at tier 1 until the referrer activates).
 */
export const getTierSegments = (referral: PerpsVolumeTieredReferral): PerpsTierSegment[] => {
    const reward = referral.referrer.volume_tiered_reward;
    const sorted = getSortedTiers(reward.tiers);

    return sorted.map((tier, index) => {
        if (tier.granted) return { key: tier.key, granted: true, fillFraction: 1 };

        const prevThreshold = index > 0 ? sorted[index - 1].threshold_cents : 0;
        const denominator = tier.threshold_cents - prevThreshold;
        if (denominator <= 0) {
            return {
                key: tier.key,
                granted: false,
                fillFraction: reward.current_volume_cents >= tier.threshold_cents ? 1 : 0,
            };
        }
        const fraction = (reward.current_volume_cents - prevThreshold) / denominator;
        return { key: tier.key, granted: false, fillFraction: Math.min(Math.max(fraction, 0), 1) };
    });
};

/**
 * Day-count display copy shared by the invited-friend card ("Expires {…}") and the per-friend
 * modal ("Tier {n} offer expires {…}").
 */
export const formatExpiresInDays = (days: number): string => {
    if (days === 0) return 'today';
    if (days === 1) return 'in 1 day';
    return `in ${days} days`;
};

export const formatDurationFromDays = (days: number): string => {
    if (days >= 365) {
        const years = Math.round(days / 365);
        return years === 1 ? 'a year' : `${years} years`;
    }
    if (days >= 30) {
        const months = Math.round(days / 30);
        return months === 1 ? 'a month' : `${months} months`;
    }
    if (days >= 7) {
        const weeks = Math.round(days / 7);
        return weeks === 1 ? 'a week' : `${weeks} weeks`;
    }
    return days === 1 ? 'a day' : `${days} days`;
};

export const formatBpsAsPercent = (bps: number): string => `${bps / 100}%`;
